import { Box, Button, Chip, Stack } from "@mui/material";
import { ChatChannel } from "../chat.types";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { Text, useModalState } from "@clipboard-health/ui-react";
import { PlacementDialog } from "@src/appV2/Placements/components/PlacementDialog";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { getCandidateUrlPath, getUserType } from "@src/appV2/PlacementCandidates/utils";
import { PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS } from "@src/appV2/PlacementCandidates/utils";
import {
  canFetchPlacementApplications,
  transformResponseIntoPlacementApplication,
  useFetchPaginatedPlacementApplications,
} from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { isDefined } from "@clipboard-health/util-ts";
import { WorkplaceCreateInterview } from "@src/appV2/Placements/components/WorkplaceCreateInterview";
import { formatDateTime, formatTime } from "@clipboard-health/date-time";
import { InterviewStatus } from "@src/appV2/Placements/types/fetchPlacements.schema";
import { PlacementApplicationStatus } from "@src/appV2/PlacementApplications/types/fetchPlacementApplications.schema";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { useIsScheduleInterviewByWorkplace } from "@src/appV2/PlacementApplications/useIsScheduleInterviewByWorkplace";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { PlacementAccessControlStatus } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { PlacementChannelStatusChip } from "./PlacementChannelStatusChip";

interface PlacementChatHeaderProps {
  currentChannel: ChatChannel;
  channelUrl: string;
  backAction?: boolean;
}

export function PlacementChatHeader(props: PlacementChatHeaderProps) {
  const { admin, profile, user } = useSession();
  const { currentChannel, backAction, channelUrl } = props;
  const placementListingModalState = useModalState();
  const createInterviewModalState = useModalState();
  const history = useHistory();
  const channelMetadata = currentChannel.metadata;

  const { accessLevel, isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();

  const canCreateOrEditPlacements =
    (!profile.placementSettings?.managedByParentWorkplace &&
      (accessLevel === PlacementAccessControlStatus.EDIT_PAID ||
        accessLevel === PlacementAccessControlStatus.EDIT_FREE)) ||
    admin;
  const isScheduleInterviewByWorkplace = useIsScheduleInterviewByWorkplace();

  const { workplaces } = useManagedWorkplaces();

  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;
  const eventContext = {
    workplaceId,
    managingWorkplaceId: channelMetadata?.managingFacilityId,
    workerId,
    placementId,
    userId: user?._id,
    userType: getUserType(profile, admin),
    userEmail: user?.email,
    source: "chatHeader",
  };

  const { data: placementApplicationsData, refetch: refetchPlacementApplications } =
    useFetchPaginatedPlacementApplications(
      {
        workplaceId: profile.userId,
        queryParams: {
          filter: {
            "placement.id": placementId,
            "worker.id": workerId,
            "workplace.id": workplaces.map((workplace) => workplace.id).join(","),
          },
        },
      },
      {
        enabled:
          isDefined(profile) &&
          isPlacementsEnabled &&
          canFetchPlacementApplications(user?.access ?? []),
      }
    );

  const placementApplications = placementApplicationsData?.pages.flatMap(
    (placementApplicationsPage) =>
      placementApplicationsPage.data.map((placementApplication) =>
        transformResponseIntoPlacementApplication(
          placementApplication,
          placementApplicationsPage.included
        )
      )
  );

  const currentPlacementApplication = placementApplications?.at(0);

  const bookedInterview = currentPlacementApplication?.interviews.find(
    (interview) =>
      interview.status === InterviewStatus.BOOKED &&
      interview.type === InterviewType.IN_PERSON_INTERVIEW
  );

  const hasPendingInterviewRequests =
    isScheduleInterviewByWorkplace &&
    isDefined(currentPlacementApplication) &&
    [PlacementApplicationStatus.APPLIED, PlacementApplicationStatus.ACCEPTED].includes(
      currentPlacementApplication.status
    ) &&
    !isDefined(bookedInterview);

  const hasPreScreenInterview = currentPlacementApplication?.interviews.some(
    (interview) =>
      interview.type === InterviewType.PHONE_SCREEN && interview.status === InterviewStatus.BOOKED
  );

  return (
    <>
      <Box className="custom-header" sx={{ width: "100%" }}>
        <Box className="custom-header-title" sx={{ width: "100%" }}>
          {backAction && (
            <ArrowLeftOutlined
              className="close-channel"
              onClick={() => history.push(getLocation("facilityChat"))}
            />
          )}

          <Stack
            alignItems="center"
            className="custom-header-title-content"
            justifyContent="space-between"
            sx={{
              width: { xs: "100%" },
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Stack
              spacing={2}
              direction="row"
              className="custom-header-title-content-left"
              sx={{
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: { xs: "space-between", md: "flex-start" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  flexDirection: { xs: "column" },
                  alignItems: { xs: "flex-start", md: "flex-start" },
                  justifyContent: { xs: "space-between", md: "flex-start" },
                }}
              >
                <Text variant="body1" color="text.primary" bold>
                  {channelMetadata?.hcpName ?? ""}
                </Text>
                <Text variant="body1" color="text.primary">
                  Job listing:
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      textDecoration: "underline",
                      color: (theme) => theme.palette.primary.main,
                      textUnderlineOffset: 2,
                    }}
                    onClick={() => {
                      logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
                        ...eventContext,
                        action: "viewListingButtonClick",
                      });
                      placementListingModalState.openModal();
                    }}
                  >
                    {channelMetadata?.placementTitle}
                  </Button>
                </Text>
              </Stack>
              <Box sx={{ pr: { xs: 3, md: 0 } }}>
                <PlacementChannelStatusChip
                  placementApplication={currentPlacementApplication}
                  channelUrl={channelUrl}
                />
              </Box>
            </Stack>
            <Stack
              className="custom-header-title-content-right"
              sx={{
                flexDirection: { xs: "column", md: "row" },
                width: { xs: "100%", md: "auto" },
                mt: { xs: 2, md: 0 },
                alignItems: { xs: "flex-start", md: "center" },
                gap: { xs: 2, md: 1 },
              }}
              flexWrap="wrap"
            >
              {channelMetadata?.placementCandidateAccessId && (
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    width: { xs: "100%", md: "auto" },
                  }}
                  onClick={() => {
                    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
                      ...eventContext,
                      action: "viewProfileButtonClick",
                    });
                    window.open(
                      getCandidateUrlPath(
                        channelMetadata.placementCandidateAccessId!,
                        profile?.userId,
                        PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS.PLACEMENT_CANDIDATE_CHAT_HEADER
                      ),
                      "_blank"
                    );
                  }}
                >
                  <Text variant="caption" color={(theme) => theme.palette.primary.main}>
                    View profile
                  </Text>
                </Button>
              )}

              {(hasPreScreenInterview || hasPendingInterviewRequests) &&
                !isDefined(bookedInterview) &&
                canCreateOrEditPlacements && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
                        ...eventContext,
                        action: "scheduleInterviewButtonClick",
                      });
                      createInterviewModalState.openModal();
                    }}
                  >
                    <Text variant="caption" color={(theme) => theme.palette.common.white}>
                      Schedule interview
                    </Text>
                  </Button>
                )}

              {isDefined(currentPlacementApplication) && bookedInterview && (
                <Chip
                  label={`Interview scheduled: ${formatDateTime(bookedInterview.start, {
                    timeZone: profile.tmz,
                  })} - ${formatTime(bookedInterview.end, {
                    timeZone: profile.tmz,
                  })}`}
                  size="medium"
                  variant="filled"
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: 400,
                    borderColor: (theme) => theme.palette.primary.main,
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
      {placementListingModalState.modalIsOpen && channelMetadata?.placementId && (
        <PlacementDialog
          modalState={placementListingModalState}
          placementId={channelMetadata?.placementId}
        />
      )}
      {createInterviewModalState.modalIsOpen &&
        isDefined(placementApplications) &&
        placementApplications.length > 0 && (
          <WorkplaceCreateInterview
            modalState={createInterviewModalState}
            placementApplicationId={placementApplications[0].id}
            workplaceId={workplaceId ?? ""}
            workerId={workerId ?? ""}
            onInterviewCreated={() => {
              refetchPlacementApplications();
            }}
          />
        )}
    </>
  );
}
